import mixpanel from 'mixpanel-browser';

export const STORAGE_KEY = 'elli.acquisition';

const PREFIX_ALLOW = /^utm_[a-z0-9_]+$/;
const EXPLICIT_ALLOW = new Set(['interest', 'gclid', 'fbclid', 'ref', 'referrer']);
const MAX_VALUE_LEN = 200;
const MAX_KEYS = 25;

export interface AcquisitionData {
  params: Record<string, string>;
  first_seen_at: string;
  landing_path: string;
  referrer: string | null;
}

function isAllowedKey(key: string): boolean {
  return PREFIX_ALLOW.test(key) || EXPLICIT_ALLOW.has(key);
}

function readParamsFromUrl(): Record<string, string> {
  const out: Record<string, string> = {};
  const search = new URLSearchParams(window.location.search);
  for (const [key, value] of search.entries()) {
    if (!isAllowedKey(key)) continue;
    if (typeof value !== 'string') continue;
    if (value.length === 0 || value.length > MAX_VALUE_LEN) continue;
    if (out[key] !== undefined) continue;
    out[key] = value;
    if (Object.keys(out).length >= MAX_KEYS) break;
  }
  return out;
}

export function captureMarketingParams(): AcquisitionData | null {
  if (typeof window === 'undefined') return null;

  const existing = getStoredAcquisition();
  if (existing) return existing;

  const params = readParamsFromUrl();
  if (Object.keys(params).length === 0) return null;

  const data: AcquisitionData = {
    params,
    first_seen_at: new Date().toISOString(),
    landing_path: window.location.pathname,
    referrer: document.referrer || null,
  };

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // sessionStorage disabled — fail open, still register Mixpanel
  }

  try {
    mixpanel.register(params);
    mixpanel.track('Marketing Visit Captured', {
      ...params,
      landing_path: data.landing_path,
      referrer: data.referrer,
    });
  } catch {
    // Mixpanel not initialised yet (e.g. no token in dev) — silent
  }

  return data;
}

export function getStoredAcquisition(): AcquisitionData | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AcquisitionData;
    if (!parsed || typeof parsed !== 'object' || !parsed.params) return null;
    return parsed;
  } catch {
    return null;
  }
}
